import {
  ClientEntity,
  OperationalEntity
} from "@common-core/coat-operational-hierarchy-appsync-model";

export interface HierarchyTenantWarning {
  type: "multiple-tenants";
  tenantIdUsedForCreation: string;
  tenantIds: string[];
}

export interface HierarchyTenantResolution {
  tenantId?: string;
  warning?: HierarchyTenantWarning;
}

/**
 * Validates tenant data integrity across the hierarchy and extracts the tenant ID
 * from the ultimate parent's mapped operational entities.
 *
 * Resolution algorithm (see tenant-resolution-logic.md for full spec):
 * 1. Collect all tenant IDs from the hierarchy tree.
 * 2. >1 unique tenant IDs → If ultimate parent resolves to exactly one tenant, return it with a warning. Otherwise error.
 * 3. Exactly 1 tenant ID found → If on ultimate parent's OE, return it. Otherwise, return error.
 * 4. 0 tenant IDs found → If ultimate parent has OEs without tenancy, error. If no OEs, return undefined (new tenant needed).
 *
 * @param source - The root client entity of the hierarchy being created
 * @returns The tenant resolution with tenant ID and optional warning metadata
 * @throws Error if tenancy cannot be established or ultimate parent cannot resolve to a single tenant
 */
export const validateAndExtractTenantIdForHierarchy = (
  source: ClientEntity
): HierarchyTenantResolution => {
  const ultimateParent = source.hierarchy || source;

  const extractTenantIds = (entities?: OperationalEntity[] | null): string[] =>
    (entities || [])
      .map(oe => oe.tenantId)
      .filter((id): id is string => id != null && id !== "");

  const collectAllTenantIds = (entity: ClientEntity): string[] => {
    const tenantIds: string[] = [];
    tenantIds.push(...extractTenantIds(entity.mappedOperationalEntities));
    if (entity.children) {
      entity.children.forEach(child => {
        tenantIds.push(...collectAllTenantIds(child));
      });
    }
    return tenantIds;
  };

  // Step 1: Collect all tenant IDs across the hierarchy
  const allTenantIds = collectAllTenantIds(ultimateParent);
  const uniqueTenantIds = Array.from(new Set(allTenantIds));
  const uniqueUltimateParentTenantIds = Array.from(
    new Set(extractTenantIds(ultimateParent.mappedOperationalEntities))
  );

  // Step 2: Multiple tenant IDs → warning if ultimate parent resolves to one, error otherwise
  if (uniqueTenantIds.length > 1) {
    if (uniqueUltimateParentTenantIds.length === 1) {
      const tenantId = uniqueUltimateParentTenantIds[0];
      return {
        tenantId,
        warning: {
          type: "multiple-tenants",
          tenantIdUsedForCreation: tenantId,
          tenantIds: uniqueTenantIds
        }
      };
    }

    throw new Error(
      `This client hierarchy is associated with multiple tenants, and the ultimate parent does not resolve to a single tenant. Please contact COAT support. Associated tenants: ${uniqueTenantIds.join(", ")}`
    );
  }

  // Step 3: Exactly one tenant ID → validate it's on the ultimate parent
  if (uniqueTenantIds.length === 1) {
    const tenantId = uniqueTenantIds[0];

    if (uniqueUltimateParentTenantIds.includes(tenantId)) {
      return {tenantId};
    }

    throw new Error(
      `Data integrity error for ${source.caId}. Tenant ID "${tenantId}" exists only on descendant entities, not on the ultimate parent. Tenancy must be established at the ultimate parent level.`
    );
  }

  // Step 4: Zero tenant IDs → check if ultimate parent has OEs
  const mappedEntities = ultimateParent.mappedOperationalEntities;

  if (mappedEntities && mappedEntities.length > 0) {
    throw new Error(
      `Data integrity error for ${source.caId}. The ultimate parent has mapped operational entities, but none have tenancy established.`
    );
  }

  return {tenantId: undefined};
};
